import React from 'react';
import { NextPage } from 'next';
import {
  Button, ButtonColor, SpacingName,
  TypographyName, Image,
  Grid,
  Typography,
  FontWeight,
} from '@doveit/lego';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import LandingLayout from '../client/layouts/landing-layout/LandingLayout';
import Hero from '../client/templates/common/hero/Hero';
import { CTA, HeroSection, TextAlign } from '../client/types';
import { requireFromStaticAsset, requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import CreateEvaluation from '../client/containers/create-evaluation/CreateEvaluation';
import { sendEvaluationFormSubmitEventAndRedirect } from '../client/utils/tracking';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import PageSection from '../client/components/page-section/PageSection';
import { LogosSectionProps } from '../client/templates/common/logos-section/LogosSection';
import LogoList from '../client/components/logo-list/LogoList';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import { buildSoldPropertiesURLObject } from '../client/utils/links';
import ListReviews from '../client/containers/list-reviews/ListReviews';
import { ReviewTarget } from '../domain/graphcms';
import FAQ, { FaqProps } from '../client/templates/common/faq/Faq';
import * as styles from '../client/templates/landing-city-seller-2/LandingCitySeller2.style';
import SavingCalculator from '../client/components/saving-calculator/SavingCalculator';
import ViewLatestSoldProperties from '../client/containers/view-latest-sold-properties/ViewLatestSoldProperties';
import StripesSection from '../client/sections/stripes-section/StripesSection';
import FeatureBlock from '../client/blocks/feature-block/FeatureBlock';
import StatsBlock from '../client/components/stats-block/StatsBlock';
import { AppNavigationVariant } from '../client/templates/common/app-navigation/types';

interface WhyChooseUsStat {
  id: string,
  title: string,
  content: string,
}

interface WhyChooseUsStatsBlock {
  image: {
    url: string,
    alt: string,
  },
  stats: WhyChooseUsStat[],
}

interface WhyChooseUsContentBlock {
  badge: string,
  title: string,
  content: string,
  image: {
    url: string,
    alt: string,
  },
}

export interface LandingCitySeller2Props {
  SEO: PageHeadProps['SEO'],
  heroSection: HeroSection,
  pressSection: LogosSectionProps & { cta?: CTA },
  whyChooseUsSection: {
    title: string,
    blockStats: WhyChooseUsStatsBlock,
    blockZeroCommissioni: WhyChooseUsContentBlock,
  },
  savingSection: {
    title: string,
    description: string,
    cta: CTA,
    initialPropertyValue: number,
  },
  preUspSection: {
    title: string,
    items: {
      id: string,
      title: string,
      description: string,
      image: {
        url: string,
        alt: string,
      },
    }[],
    cta: CTA,
  },
  uspSection: {
    title: string,
    subtitle: string,
    items: {
      id: string,
      title: string,
      description: string,
    }[],
  },
  latestSoldPropertiesSection: {
    title: string,
    cta: CTA,
  },
  reviewsSection: {
    title: string,
    description: string,
  },
  aboutUsSection: {
    title: string,
    content: string,
  },
  faqSection: FaqProps & {
    footer: {
      title: string,
      content: string,
      cta: CTA,
    },
  },
  footNotesSection: {
    items: {
      id: string,
      text: string,
    }[],
  },
}

export const LandingCitySeller2: NextPage<LandingCitySeller2Props> = ({
  SEO,
  heroSection,
  pressSection,
  whyChooseUsSection,
  savingSection,
  preUspSection,
  uspSection,
  latestSoldPropertiesSection,
  reviewsSection,
  aboutUsSection,
  faqSection,
  footNotesSection,
}) => {
  const { current, ref: formRef } = useRefInView({
    threshold: 0,
  });

  return (
    <LandingLayout navigationVariant={AppNavigationVariant.LIGHT}>
      <PageHead SEO={SEO} />

      <Hero
        title={heroSection.title}
        description={heroSection.description}
        secondarySlotTitle={heroSection.formTitle}
        backgroundImage={requireResourcesFromStaticAssets(heroSection.backgroundImage)}
        primarySlot={(
          <div css={styles.heroSectionPrimarySlot}>
            <Grid gutter={SpacingName.SMALLER}>
              <Grid.Unit
                size={{ SM: 1 / 4, XS: 1 / 2 }}
                css={styles.heroSectionPrimarySlotGridUnit}
              >
                <div>
                  <Typography.H3
                    tagName="p"
                    fontWeight={FontWeight.MEDIUM}
                    textAlign={TextAlign.CENTER}
                  >
                    +4.000
                  </Typography.H3>

                  <Typography.BODY
                    tagName="p"
                    textAlign={TextAlign.CENTER}
                    color="neutrals.dark"
                  >
                    Immobili venduti
                  </Typography.BODY>
                </div>
              </Grid.Unit>

              <Grid.Unit
                size={{ SM: 1 / 4, XS: 1 / 2 }}
                css={styles.heroSectionPrimarySlotGridUnit}
              >
                <Image
                  src={requireFromStaticAsset('google-stars.png')}
                  alt="Google"
                  lazyLoad
                  responsive
                  height="56px"
                />
              </Grid.Unit>

              <Grid.Unit
                size={{ SM: 1 / 4, XS: 1 / 2 }}
                css={styles.heroSectionPrimarySlotGridUnit}
              >
                <Image
                  src={requireFromStaticAsset('trustpilot-stars.png')}
                  alt="Trustpilot"
                  lazyLoad
                  responsive
                  height="56px"
                />
              </Grid.Unit>

              <Grid.Unit
                size={{ SM: 1 / 4, XS: 1 / 2 }}
                css={styles.heroSectionPrimarySlotGridUnit}
              >
                <Image
                  src={requireFromStaticAsset('immobiliare-certificate-2025.png')}
                  alt="Certificato Immobiliare.it 2025"
                  lazyLoad
                  responsive
                  height="56px"
                />
              </Grid.Unit>
            </Grid>
          </div>
        )}
        secondarySlot={(
          <div ref={formRef}>
            <CreateEvaluation
              onSubmit={() => sendEvaluationFormSubmitEventAndRedirect('hero:create-evaluation')}
            />
          </div>
        )}
      />

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title
            textAlign={TextAlign.CENTER}
            content={pressSection.title}
          />
          <PageSection.Block>
            <LogoList
              items={pressSection.items
                .map((item) => ({
                  ...item,
                  picture: requireFromStaticAsset(item.picture),
                }))}
              inlineItems={6}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title
            textAlign={TextAlign.CENTER}
            content={whyChooseUsSection.title}
          />

          <PageSection.Block>
            <StatsBlock
              imageUrl={requireFromStaticAsset(whyChooseUsSection.blockStats.image.url)}
              imageAlt={whyChooseUsSection.blockStats.image.alt}
              items={whyChooseUsSection.blockStats.stats.map((item) => ({
                title: item.title,
                decription: item.content,
              }))}
            />
          </PageSection.Block>
          <PageSection.Block>
            <FeatureBlock
              title={{
                tag: 'h2',
                content: whyChooseUsSection.blockZeroCommissioni.badge,
              }}
              subtitle={whyChooseUsSection.blockZeroCommissioni.title}
              description={whyChooseUsSection.blockZeroCommissioni.content}
              contentCentered
            >
              <Image
                src={requireFromStaticAsset(whyChooseUsSection.blockZeroCommissioni.image.url)}
                alt={whyChooseUsSection.blockZeroCommissioni.image.alt}
                lazyLoad
              />
            </FeatureBlock>
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title
            content={savingSection.title}
            textAlign={TextAlign.CENTER}
          />

          <PageSection.Description
            text={savingSection.description}
            size={TypographyName.H4}
            textAlign={TextAlign.CENTER}
            color="neutrals.dark"
          />

          <PageSection.Block>
            <SavingCalculator />
          </PageSection.Block>

          <PageSection.Actions textAlign={TextAlign.CENTER}>
            <Button
              {...savingSection.cta}
              color={ButtonColor.PRIMARY}
              onClick={scrollToRef(current, { behavior: 'smooth' })}
            />
          </PageSection.Actions>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <StripesSection
        title={preUspSection.title}
        items={preUspSection.items.map(({
          id, title, description, image,
        }) => ({
          id,
          title,
          content: description,
          image,
        }))}
        actions={[{
          ...preUspSection.cta,
          onClick: scrollToRef(current, { behavior: 'smooth' }),
        }]}
      />

      <StripesSection
        title={uspSection.title}
        content={uspSection.subtitle}
        items={uspSection.items.map(({ id, title, description }) => ({ id, title, content: description }))}
        showAsGrid
      />

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title
            textAlign={TextAlign.CENTER}
            content={latestSoldPropertiesSection.title}
          />
        </PageSection.Wrapper>

        <PageSection.Wrapper withoutPad>
          <PageSection.Block>
            <ViewLatestSoldProperties sideSpacing={SpacingName.SMALL} />
          </PageSection.Block>
        </PageSection.Wrapper>

        <PageSection.Wrapper>
          <PageSection.Actions textAlign={TextAlign.CENTER}>
            <Button
              dataRef="latest-sold-section-button"
              dataTracking="latest-sold-section-button"
              color={ButtonColor.PRIMARY}
              label="Vendi il tuo immobile"
              aria-label="Vendi il tuo immobile"
              href={buildSoldPropertiesURLObject().as}
              onClick={scrollToRef(current, { behavior: 'smooth' })}
            />
          </PageSection.Actions>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title
            textAlign={TextAlign.CENTER}
            content={reviewsSection.title}
          />
          <PageSection.Description
            textAlign={TextAlign.CENTER}
            text={reviewsSection.description}
            size={TypographyName.H4}
            color="neutrals.dark"
          />
        </PageSection.Wrapper>
        <PageSection.Wrapper withoutPad>
          <PageSection.Block>
            <ListReviews
              target={[ReviewTarget.COMPRATORE]}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title
            textAlign={TextAlign.CENTER}
            content={aboutUsSection.title}
          />
          <PageSection.Description
            textAlign={TextAlign.CENTER}
            size={TypographyName.H4}
            text={aboutUsSection.content}
            color="neutrals.dark"
          />
        </PageSection.Wrapper>
      </PageSection>

      <FAQ
        title={faqSection.title}
        description={faqSection.description}
        items={faqSection.items}
        additionalContent={(
          <PageSection.Wrapper>
            <PageSection.Block>
              <PageSection.Title
                textAlign={TextAlign.CENTER}
                size={TypographyName.H3}
                content={faqSection.footer.title}
              />

              <PageSection.Description
                textAlign={TextAlign.CENTER}
                size={TypographyName.H4}
                text={faqSection.footer.content}
                color="neutrals.dark"
              />

              <PageSection.Actions textAlign={TextAlign.CENTER}>
                <Button
                  dataRef={faqSection.footer.cta.dataRef}
                  dataTracking={faqSection.footer.cta.dataTracking}
                  color={ButtonColor.PRIMARY}
                  label={faqSection.footer.cta.label}
                  aria-label={faqSection.footer.cta['aria-label']}
                  onClick={scrollToRef(current, { behavior: 'smooth' })}
                />
              </PageSection.Actions>
            </PageSection.Block>
          </PageSection.Wrapper>
        )}
      />

      <PageSection>
        <PageSection.Wrapper>
          {footNotesSection.items.map((item) => (
            <Typography.SMALL
              key={item.id}
              color="neutrals.dark"
              fontWeight={FontWeight.LIGHT}
            >
              {item.text}
            </Typography.SMALL>
          ))}
        </PageSection.Wrapper>
      </PageSection>
    </LandingLayout>
  );
};

export const getServerSideProps = withGetServerSideError<LandingCitySeller2Props>(async () => {
  const [common, landingCitySeller2] = await Promise.all([
    importDynamicData('common.yml'),
    importDynamicData('pages/landing-city-seller-2.yml'),
  ]);

  return {
    props: {
      ...common,
      ...landingCitySeller2,
      pressSection: {
        ...landingCitySeller2.pressSection,
        items: common.press.items,
      },
    },
  };
});

export default withError(LandingCitySeller2);
